import { createAsyncThunk, createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import moment, { now } from 'moment';
import { RootState, AppThunk } from './app/store';
import { Icons } from './components/Icon/Icon';
import axios from 'axios';

import { endpoints, GatewayIp } from './data/endpoints';
import { set, uniqueId, update } from 'lodash';
import { IWatchfolder, IWatchfolderEngine, IWatchfolderEngineProfile, IWatchfolderResult } from './components/services/watchfolder/Watchfolder.model';
import { FileQueuerJob, IFileQueuerConfig, IFileQueuerLocation, IFileQueuerLocationContent } from './components/services/fileQueuer/FileQueuer.model';
import { emptyIngestJobResult, IIngestJob, IIngestJobResult, IIngestLocation, IIngestLocationContent, IIngestMetadata } from './components/services/ingest/Ingest.model';
import { emptyDrawRecord, emptyRecord, ICalendarViewMode, IDrawEvent, IRecord, IViewMode } from './components/inputs/Timeline/Timeline.model';
import { IJobsResponse, JobStatuses } from './components/services/oneGUI/data.model';
import { isServiceEnabledByBuild } from './config/build';

export const services: { [key: string]: { name: string; icon: any } } = {
  'home': { name: "Home", icon: Icons.home },
  'oneGUI': { name: "Job Monitor", icon: Icons.oneGUI },
  'watchfolder': { name: "Watchfolder", icon: Icons.watchfolder },
  'fileQueuer': { name: "File Queuer", icon: Icons.fileQueuer },
  'ingest': { name: "Ingest", icon: Icons.ingest },
  'recorder': { name: "Recorder", icon: Icons.circle },
  'complianceMonitoring': { name: "Multiviewer", icon: Icons.monitor },
  'compliancePlayer': { name: "Player", icon: Icons.control_play },
  'complianceRecord': { name: "Encoder", icon: Icons.video },
}

export type IRole = 'ROLE_ADMIN' | 'ROLE_WATCHFOLDER' | 'ROLE_FILEQUEUER' | 'ROLE_INGEST' | 'ROLE_JOBMONITOR' | 'ROLE_RECORDER' | 'ROLE_COMPLIANCE_PLAYER' | 'ROLE_COMPLIANCE_RECORD' | 'ROLE_COMPLIANCE_MONITORING';

export interface IScheduleRetryPolicy {
  maxAttempts?: number;
  backoff?: string;
  initialMs?: number;
  [key: string]: any;
}

export interface ISchedule {
  id?: string;
  slotId?: string;
  label?: string;
  taskType?: string;
  params?: Record<string, any>;
  startAt?: string;
  endAt?: string;
  tz?: string;
  overlapPolicy?: string;
  misfirePolicy?: string;
  retryPolicy?: IScheduleRetryPolicy;
  enabled?: boolean;
  status?: number | string;
  createdBy?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface ISchedulerProfile {
  id: string;
  name: string;
  category?: string;
}

export interface ISchedulerDeviceFormat {
  index: number;
  [key: string]: any;
}

export interface ISchedulerDevice {
  index: number;
  name: string;
  deviceToken?: string;
  videoFormats: ISchedulerDeviceFormat[];
  audioFormats: ISchedulerDeviceFormat[];
}

export interface ISchedulerDeviceSection {
  name: string;
  devices: ISchedulerDevice[];
}

export interface ISchedulerSlot {
  id: string;
  label: string;
  enabled: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface ISlotDefaultConfig {
  id: string;
  slotId: string;
  taskType: string;
  params: Record<string, any>;
  enabled: boolean;
  tz?: string;
  overlapPolicy?: string;
  misfirePolicy?: string;
  retryPolicy?: IScheduleRetryPolicy;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IMe { username: string; name: string; isLogin: boolean; roles: IRole[]; permissions: string[]; }
export interface AppState {
  user: any;
  me: IMe;
  token: string;
  sessionExpired: boolean;
  connected: Boolean;
  wfResult: any;
  config: any;
  permissions: string[];
  status: 'idle' | 'loading' | 'failed';
  initializing: boolean;
  scrHeight: number;
  updateActionStamp: number;
  selectedTab: string;
  filteredServices: (keyof typeof services)[];
  selectedService: keyof typeof services;
  oneGUITasks: IJobsResponse;
  watchfolderResult: IWatchfolderResult;
  statusOneGUI: 'idle' | 'loading' | 'failed';
  statusWatchfolder: 'idle' | 'loading' | 'failed';
  watchfolderEngines: IWatchfolderEngine[];
  watchfolderEngineProfiles: { [key: string]: IWatchfolderEngineProfile[] };
  filequeuerResult?: any; // Placeholder for file queuer result
  filequeuerLocations?: IFileQueuerLocation[];
  fileQueuerContent: { [path: string]: IFileQueuerLocationContent[] };
  selectedFileQueuerLocation?: string | null;
  selectedFileQueuerPath?: string | null;
  fileQueuerTagetConfigs: IFileQueuerConfig;

  ingestResult: IIngestJobResult;
  statusIngest: 'idle' | 'loading' | 'failed';
  ingestLocations: IIngestLocation[];
  ingestContent: { [path: string]: IIngestLocationContent[] };
  selectedIngestLocation?: string | null;
  selectedIngestPath?: string | null;

  schedules: ISchedule[];
  schedulerSlots: ISchedulerSlot[];
  slotActiveConfigs: Record<string, ISlotDefaultConfig | null>;
  schedulerProfiles: ISchedulerProfile[];
  schedulerDeviceCatalogue: ISchedulerDeviceSection[];
  selectedSchedule: ISchedule | null;
  schedulerStatus: 'idle' | 'loading' | 'failed';
  schedulerError: string | null;

  records: IRecord[],
  editedRecords: { [id: string]: IRecord }

  selectedRecordId: string,
  zoom: number[];
  zoomFactor: number;
  scrollPosition: number;
  nowPosition: number;
  dragItemId: string | null;
  selectedChannel: string | null,
  selectedDate: string;
  calendarViewMode: ICalendarViewMode;
  drawRecord: IDrawEvent;
  drawMode: boolean;
  viewMode: IViewMode;
}

const initialState: AppState = {
  user: null,
  me: null,
  token: null,
  sessionExpired: false,
  initializing: true,
  connected: false,
  wfResult: null,
  config: null,
  permissions: [],
  status: 'idle',
  scrHeight: 0,
  updateActionStamp: null,
  selectedTab: null,
  selectedService: null,
  oneGUITasks: {
    items: [],
    totalsByStatus: {},
    total: 0,
    page: 0,
    size: 10,
  },
  watchfolderResult: {
    content: [],
    empty: true,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    sort: { empty: true, sorted: true, unsorted: false },
    totalElements: 0,
    totalPages: 0,
    pageable: {
      pageNumber: 0,
      pageSize: 0,
      sort: { empty: true, sorted: true, unsorted: false },
      offset: 0,
      paged: true,
      unpaged: true,
    },
    size: 0,
  },
  filteredServices: [],
  statusOneGUI: 'idle',
  statusWatchfolder: 'idle',
  watchfolderEngines: [],
  watchfolderEngineProfiles: {},
  fileQueuerContent: {},
  filequeuerResult: null,
  filequeuerLocations: [],
  selectedFileQueuerLocation: null,
  selectedFileQueuerPath: null,
  fileQueuerTagetConfigs: {
    file: "",
    resourceId: "",
    targetService: null,
    config: {}
  },
  ingestResult: emptyIngestJobResult,
  statusIngest: 'idle',
  ingestLocations: [],
  ingestContent: {},
  selectedIngestLocation: null,
  selectedIngestPath: null,
  schedules: [],
  schedulerSlots: [],
  slotActiveConfigs: {},
  schedulerProfiles: [],
  schedulerDeviceCatalogue: [],
  selectedSchedule: null,
  schedulerStatus: 'idle',
  schedulerError: null,
  records: [],
  editedRecords: {},
  selectedDate: "",
  selectedRecordId: null,
  zoom: [30, 70],
  zoomFactor: 0.83,
  scrollPosition: 0,
  nowPosition: 0,
  selectedChannel: "",
  dragItemId: null,
  drawRecord: emptyDrawRecord,
  calendarViewMode: "day",
  drawMode: false,
  viewMode: "calendar",
};

export const defaultScheduleMetadata: Partial<ISchedule> & {
  params: Record<string, any>;
  retryPolicy: IScheduleRetryPolicy;
} = {
  label: "",
  taskType: "MEDIAENGINE_RECORD",
  params: {
    monitorId: "",
    input: {
      type: "rtmp",
      url: "",
      video: {
        sourceId: "",
        formatId: null,
      },
      audio: {
        sourceId: "",
        formatId: null,
      },
    },
    output: {
      type: "file",
      url: "",
    },
    profile: "",
    realtime: true,
    additionalArgs: [],
  },
  tz: "Europe/Vienna",
  overlapPolicy: "SKIP_IF_RUNNING",
  misfirePolicy: "START_IF_BEFORE_END",
  retryPolicy: { maxAttempts: 3, backoff: "exponential", initialMs: 1000 },
  enabled: true,
};

const normalizeScheduleStatus = (status: number | string | undefined, enabled?: boolean): number => {
  if (typeof status === 'number' && Number.isFinite(status)) {
    return status;
  }

  if (typeof status === 'string') {
    const parsed = Number(status);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    switch (status.toLowerCase()) {
      case 'enabled':
      case 'active':
      case 'running':
        return 1;
      case 'disabled':
      case 'inactive':
        return 0;
      default:
        break;
    }
  }

  return enabled ? 1 : 0;
};

const extractMonitorIdFromSchedule = (schedule: ISchedule): string => {
  if (schedule.slotId?.trim()) return schedule.slotId.trim();

  const monitorCandidates = [
    schedule.params?.monitorId,
    schedule.params?.monitor,
    schedule.params?.input?.video?.sourceId,
  ];

  const monitorId = monitorCandidates
    .map((value) => (value ?? '').toString().trim())
    .find((value) => value.length > 0) ?? '';

  if (monitorId) {
    return monitorId;
  }

  const additionalArgs = schedule.params?.additionalArgs;
  if (!Array.isArray(additionalArgs)) {
    return "";
  }

  const monitorArg = additionalArgs.find(
    (arg) =>
      arg &&
      typeof arg === 'object' &&
      !Array.isArray(arg) &&
      typeof arg.monitorId === 'string' &&
      arg.monitorId.trim().length > 0
  );

  return monitorArg?.monitorId?.toString().trim() ?? "";
};

const buildScheduleMetadata = (schedule: ISchedule): Record<string, any> => {
  const monitorId = extractMonitorIdFromSchedule(schedule);
  const inputUrl = (
    schedule.params?.input?.url ??
    schedule.params?.inputUrl ??
    (typeof schedule.params?.input === 'string' ? schedule.params?.input : '') ??
    ''
  ).toString();

  return {
    ...defaultScheduleMetadata,
    ...schedule,
    params: {
      ...(defaultScheduleMetadata.params ?? {}),
      ...(schedule.params ?? {}),
      ...(monitorId ? { monitorId } : {}),
      ...(inputUrl ? { inputUrl } : {}),
    },
    retryPolicy: {
      ...(defaultScheduleMetadata.retryPolicy ?? {}),
      ...(schedule.retryPolicy ?? {}),
    },
  };
};

const mapScheduleToRecord = (schedule: ISchedule): IRecord => {
  const metadata = {
    ...buildScheduleMetadata(schedule),
    scheduleId: schedule.id,
  };

  return {
    id: schedule.id ?? uniqueId("schedule_"),
    title: schedule.label ?? schedule.taskType ?? "Schedule",
    monitor: extractMonitorIdFromSchedule(schedule),
    metadata,
    startedAt: schedule.startAt ?? "",
    endedAt: schedule.endAt ?? "",
    status: normalizeScheduleStatus(schedule.status, schedule.enabled),
    creator: schedule.createdBy ?? "",
    updatedAt: schedule.updatedAt ?? schedule.startAt ?? "",
  };
};

const mapSchedulesToRecords = (schedules: ISchedule[]): IRecord[] =>
  (schedules ?? []).map(mapScheduleToRecord);

const normalizeSchedulerProfiles = (payload: any): ISchedulerProfile[] => {
  const data = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.profiles)
      ? payload.profiles
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.content)
        ? payload.content
        : [];

  const seen = new Set<string>();
  const profiles: ISchedulerProfile[] = [];

  data.forEach((entry: any) => {
    if (entry && typeof entry === 'object') {
      const id = [
        entry.id,
        entry.href,
        entry.profile,
      ].find((value) => typeof value === 'string' && value.trim().length > 0);
      const name = [
        entry.name,
        entry.label,
        entry.displayName,
        entry.id,
      ].find((value) => typeof value === 'string' && value.trim().length > 0);

      if (id && name && !seen.has(id)) {
        seen.add(id);
        profiles.push({
          id,
          name,
          category: typeof entry.category === 'string' ? entry.category : undefined,
        });
      }
      return;
    }

    if (typeof entry === 'string') {
      const value = entry.trim();
      if (value && !seen.has(value)) {
        seen.add(value);
        profiles.push({ id: value, name: value });
      }
    }
  });

  return profiles;
};

const normalizeSchedulerDeviceCatalogue = (payload: any): ISchedulerDeviceSection[] => {
  const sections = Array.isArray(payload?.sections) ? payload.sections : [];

  return sections
    .filter((section: any) => section && typeof section === 'object')
    .map((section: any) => {
      const devices = Array.isArray(section.devices) ? section.devices : [];
      return {
        name: (section.name ?? '').toString(),
        devices: devices
          .filter((device: any) => device && typeof device === 'object')
          .map((device: any) => ({
            index: Number(device.index),
            name: (device.name ?? device.deviceToken ?? '').toString(),
            deviceToken:
              typeof device.deviceToken === 'string' ? device.deviceToken : undefined,
            videoFormats: Array.isArray(device.videoFormats) ? device.videoFormats : [],
            audioFormats: Array.isArray(device.audioFormats) ? device.audioFormats : [],
          })),
      };
    });
};

export const initApp = createAsyncThunk(
  'app/initapp',
  async ({ token }: { token: string }) => {
    // const response = await axios.get('GatewayIP/tasks', {
    //   headers: {
    //     Authorization: `Bearer ${token}`,
    //   },
    // });
    // const tasks = response.data;
  });

export const loadTasks = createAsyncThunk(
  'app/loadTasks',
  async ({ token, page, size, status }: { token: string; page: number; size: number; status: string }) => {
    let sstatus = "";
    let totalsByStatus: Record<string, number> = {};

    if (status && status !== "All") {
      sstatus = `&status=${status}`;
    }

    const response = await axios.get(
      GatewayIp + endpoints.jobmonitor.getJobs + `?page=${page}&size=${size}${sstatus}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    // Fetch totals for each status in parallel
    const totals = await Promise.all(
      JobStatuses.map(async (status) => {
        let sstatus = "";
        if (status && status !== "All") {
          sstatus = `?status=${status}`;
        }
        const res = await axios.get(
          GatewayIp + endpoints.jobmonitor.getJobs + sstatus,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return { status, total: res.data.total };
      })
    );

    totalsByStatus = totals.reduce(
      (acc, { status, total }) => ({ ...acc, [status]: total }),
      {}
    );

    const result = { ...response.data, totalsByStatus };

    return { result };
  }
);

export const fetchSchedules = createAsyncThunk(
  'app/fetchSchedules',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.scheduler.schedules,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = response.data;
    const schedules: ISchedule[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.content)
          ? data.content
          : [];

    return { schedules };
  }
);

export const fetchScheduleById = createAsyncThunk(
  'app/fetchScheduleById',
  async ({ token, scheduleId }: { token: string; scheduleId: string }) => {
    const response = await axios.get(
      `${GatewayIp + endpoints.scheduler.schedules}/${scheduleId}`,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const schedule = response.data as ISchedule;
    return { schedule };
  }
);

export const fetchSchedulerProfiles = createAsyncThunk(
  'app/fetchSchedulerProfiles',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.scheduler.profiles,
      {
        withCredentials: true,
        params: {
          refresh: false,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return { profiles: normalizeSchedulerProfiles(response.data) };
  }
);

export const fetchSchedulerDeviceCatalogue = createAsyncThunk(
  'app/fetchSchedulerDeviceCatalogue',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.scheduler.deviceCatalogue,
      {
        withCredentials: true,
        params: {
          refresh: false,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return { catalogue: normalizeSchedulerDeviceCatalogue(response.data) };
  }
);

export const fetchSchedulerSlots = createAsyncThunk(
  'app/fetchSchedulerSlots',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.scheduler.slots,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = response.data;
    const slots: ISchedulerSlot[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.content)
          ? data.content
          : [];

    return { slots };
  }
);

export const createSchedulerSlot = createAsyncThunk(
  'app/createSchedulerSlot',
  async ({ token, slot }: { token: string; slot: { label: string; enabled: boolean; description: string } }) => {
    const response = await axios.post(
      GatewayIp + endpoints.scheduler.slots,
      slot,
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      }
    );
    return { slot: response.data as ISchedulerSlot };
  }
);

export const updateSchedulerSlot = createAsyncThunk(
  'app/updateSchedulerSlot',
  async ({ token, slotId, patch }: { token: string; slotId: string; patch: { label: string; enabled: boolean; description: string } }) => {
    const response = await axios.patch(
      `${GatewayIp + endpoints.scheduler.slots}/${slotId}`,
      patch,
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      }
    );
    return { slot: response.data as ISchedulerSlot };
  }
);

export const deleteSchedulerSlot = createAsyncThunk(
  'app/deleteSchedulerSlot',
  async ({ token, slotId }: { token: string; slotId: string }) => {
    await axios.delete(
      `${GatewayIp + endpoints.scheduler.slots}/${slotId}`,
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { slotId };
  }
);

export const fetchSlotActiveConfigs = createAsyncThunk(
  'app/fetchSlotActiveConfigs',
  async ({ token, slotIds }: { token: string; slotIds: string[] }) => {
    const results = await Promise.allSettled(
      slotIds.map(async (slotId) => {
        const res = await axios.get(
          `${GatewayIp}${endpoints.scheduler.slots}/${slotId}/default-configs`,
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }
        );
        const data = res.data;
        const configs: ISlotDefaultConfig[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const activeConfig = configs.find((c) => c.validTo === null) ?? null;
        return { slotId, config: activeConfig };
      })
    );

    const map: Record<string, ISlotDefaultConfig | null> = {};
    slotIds.forEach((slotId, i) => {
      const result = results[i];
      map[slotId] = result.status === 'fulfilled' ? result.value.config : null;
    });
    return map;
  }
);

export const createSchedule = createAsyncThunk(
  'app/createSchedule',
  async ({ token, schedule }: { token: string; schedule: Partial<ISchedule> }) => {
    console.log( "createSchedule", schedule)
    const response = await axios.post(
      GatewayIp + endpoints.scheduler.schedules,
      schedule,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log( "createSchedule_res", response)

    const createdSchedule = response.data as ISchedule;
    return { schedule: createdSchedule };
  }
);

export const updateSchedule = createAsyncThunk(
  'app/updateSchedule',
  async ({ token, scheduleId, schedulePatch }: { token: string; scheduleId: string; schedulePatch: Partial<ISchedule> }) => {
    const response = await axios.patch(
      `${GatewayIp + endpoints.scheduler.schedules}/${scheduleId}`,
      schedulePatch,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const updatedSchedule = response.data as ISchedule;
    return { schedule: updatedSchedule };
  }
);

export const deleteSchedule = createAsyncThunk(
  'app/deleteSchedule',
  async ({ token, scheduleId }: { token: string; scheduleId: string }) => {
    await axios.delete(
      `${GatewayIp + endpoints.scheduler.schedules}/${scheduleId}`,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return { scheduleId };
  }
);


export const loadWatchfolders = createAsyncThunk(
  'app/loadWatchfolders',
  async ({ token, page, size }: { token: string; page: number; size: number; }) => {
    console.log("call: ", GatewayIp + endpoints.watchfolder.getWatchfolders + `?page=${page}&size=${size}`);

    const response = await axios.get(
      GatewayIp + endpoints.watchfolder.getWatchfolders + `?page=${page}&size=${size}`,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const watchfolderResult = response.data;
    return { watchfolderResult }
  });

export const loadFileQueuerLocations = createAsyncThunk(
  'app/loadFileQueuerLocations',
  async ({ token }: { token: string; page: number; size: number; }) => {
    const response = await axios.get(
      GatewayIp + endpoints.resources,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const data = response.data;
    const resources: Array<{
      id: string;
      label: string;
      direction?: string;
      enabled?: boolean;
      capabilities?: string[] | null;
    }> = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.content)
          ? data.content
          : [];
    const locations = resources
      .filter((resource) => {
        const direction = (resource.direction ?? '').toUpperCase();
        return resource.enabled !== false
          && (direction === 'SOURCE' || direction === 'BOTH')
          && Array.isArray(resource.capabilities)
          && resource.capabilities.includes('FILE_BASED_READ');
      })
      .map(({ id, label }) => ({ id, label }));
    return { locations }
  });

export const loadFileQueuerLocationContent = createAsyncThunk(
  'app/loadFileQueuerLocationContent',
  async ({ token, resourceId, path }: { token: string; resourceId?: string | null; path?: string | null }) => {

    let pathUrl = path && path !== "/" ? `?path=${encodeURIComponent(path)}` : '';

    console.log(path, pathUrl)

    if (!resourceId) {
      return { content: [] as IFileQueuerLocationContent[] };
    }

    const response = await axios.get(
      GatewayIp + endpoints.filequeuer.getResourceFiles(resourceId) + pathUrl,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const content = response.data;
    return { path, content }
  });

export const submitFileQueuerJob = createAsyncThunk(
  'app/submitFileQueuerJob',
  async ({ token, fileQueuerJob }: { token: string; fileQueuerJob: FileQueuerJob }) => {

    const response = await axios.post(
      GatewayIp + endpoints.filequeuer.submitJob,
      fileQueuerJob,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const content = response.data;
    return { content }
  });

export const saveWatchfolder = createAsyncThunk(
  'app/saveWatchfolder',
  async ({ token, watchfolder }: { token: string; watchfolder: IWatchfolder; }) => {

    if (watchfolder.id === "new_0") {
      const submitted = { ...watchfolder, id: uniqueId() };
      const response = await axios.post(
        GatewayIp + endpoints.watchfolder.getWatchfolders,
        submitted,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });

      return { watchfolder: (response.data as IWatchfolder) ?? submitted, isNew: true }

    } else {
      const response = await axios.put(
        GatewayIp + endpoints.watchfolder.getWatchfolders + "/" + watchfolder.id, watchfolder,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });

      return { watchfolder: (response.data as IWatchfolder) ?? watchfolder, isNew: false }

    }
  });

export const deleteWatchfolder = createAsyncThunk(
  'app/deleteWatchfolder',
  async ({ token, ids }: { token: string; ids: string[]; }) => {
    ids.forEach(async (id: string) => {
      const response = await axios.delete(
        GatewayIp + endpoints.watchfolder.getWatchfolders + "/" + id,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });
      // console.log("delete response", response);
    })
    return { status: "OK" }
  });


export const toggleWatchfolder = createAsyncThunk(
  'app/toggleWatchfolder',
  async ({ token, ids, toDisable }: { token: string; ids: string[]; toDisable: boolean }) => {
    ids.forEach(async (id: string) => {
      const response = await axios.put(
        GatewayIp + endpoints.watchfolder.getWatchfolders + "/" + id + (toDisable ? "/disable" : "/enable"), {},
        {
          withCredentials: true,
          // credentials: "include", 
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });
      // console.log("toggleWatchfolder response", response);
    })
    return { status: "OK" }
  });

export const getWatchfolderEngines = createAsyncThunk(
  'app/getWatchfolderEngines',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.watchfolder.getEngines,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });

    // console.log("toggleWatchfolder response", response.data);
    return { engines: response.data }
  });

export const getWatchfolderEngineProfiles = createAsyncThunk(
  'app/getWatchfolderEngineProfiles',
  async ({ token, engine }: { token: string; engine: IWatchfolderEngine }) => {

    // let engines: IWatchfolderEngine[] = engine?.map(async (e: IWatchfolderEngine) => {

    try {
      const response = await axios.get(
        GatewayIp + engine.configSchema.profileEndpoint,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        })
      return { engineId: engine.id, profiles: response.data }
    } catch (error) {
      console.log("Error", error);
      return { engineId: engine.id, profiles: [] }
    }
  });


export const loadIngestJobs = createAsyncThunk(
  'app/loadIngestJobs',
  async ({ token }: { token: string }) => {
    const response = await axios.get(
      GatewayIp + endpoints.ingest.jobs,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const jobs: IIngestJob[] = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.content)
        ? response.data.content
        : [];
    const ingestResult: IIngestJobResult = {
      content: jobs,
      totalElements: jobs.length,
      totalPages: 1,
      number: 0,
      size: jobs.length,
    };
    return { ingestResult }
  });

export const submitAvidIngestJobs = createAsyncThunk(
  'app/submitAvidIngestJobs',
  async ({ token, resourceId, files, metadata }: {
    token: string;
    resourceId: string;
    files: string[];
    metadata: IIngestMetadata;
  }) => {
    const response = await axios.post(
      GatewayIp + endpoints.ingest.jobs,
      { resourceId, files, metadata },
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    return { jobs: response.data as IIngestJob[] }
  });

export const loadIngestLocations = createAsyncThunk(
  'app/loadIngestLocations',
  async ({ token }: { token: string; }) => {
    const response = await axios.get(
      GatewayIp + endpoints.resources,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const data = response.data;
    const resources = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.content)
          ? data.content
          : [];
    const locations = resources
      .filter((resource: any) => {
        const direction = (resource.direction ?? '').toUpperCase();
        return resource.enabled !== false
          && (direction === 'SOURCE' || direction === 'BOTH')
          && Array.isArray(resource.capabilities)
          && resource.capabilities.includes('FILE_BASED_READ');
      })
      .map(({ id, label }: IIngestLocation) => ({ id, label }));
    return { locations }
  });

export const loadIngestLocationContent = createAsyncThunk(
  'app/loadIngestLocationContent',
  async ({ token, locationId, path }: { token: string; locationId: string; path?: string }) => {
    let pathUrl = path && path !== "/" ? `?path=${encodeURIComponent(path)}` : '';

    if (!locationId) {
      return { path, content: [] as IIngestLocationContent[] };
    }

    const response = await axios.get(
      GatewayIp + endpoints.filequeuer.getResourceFiles(locationId) + pathUrl,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
    const content = response.data;
    return { path, content }
  });

export const AppSlice = createSlice({
  name: 'app',
  initialState,

  reducers: {
    setToken: (state, params: { payload: string }) => {
      state.token = params.payload
    },
    setInitializing: (state, params: { payload: boolean }) => {
      state.initializing = params.payload;
    },
    setSessionExpired: (state, params: { payload: boolean }) => {
      state.sessionExpired = params.payload;
    },
    setMe: (state, params: { payload: IMe }) => {
      state.me = params.payload
    },
    setScrHeight: (state, params) => {
      state.scrHeight = params.payload
    },
    setSelectedService: (state, params: { payload: keyof typeof services }) => {
      state.selectedService = params.payload
    },

    setUpdateActionStamp: (state) => {
      state.updateActionStamp = now().valueOf();
    },

    setSelectedFileQueuerLocation: (state, params: { payload: string | null }) => {
      state.selectedFileQueuerLocation = params.payload;
    },

    setSelectedFileQueuerPath: (state, params: { payload: string | null }) => {
      state.selectedFileQueuerPath = params.payload;
    },

    setFileQueuerTagetConfigs: (state, params: { payload: IFileQueuerConfig }) => {
      state.fileQueuerTagetConfigs = params.payload;
    },

    setSelectedIngestLocation: (state, params: { payload: string | null }) => {
      state.selectedIngestLocation = params.payload;
      state.selectedIngestPath = null;
      state.ingestContent = {};
    },

    setSelectedIngestPath: (state, params: { payload: string | null }) => {
      state.selectedIngestPath = params.payload;
    },

    setSelectedSchedule: (state, params: { payload: ISchedule | null }) => {
      state.selectedSchedule = params.payload;
    },

    filterServices: (state) => {
      const filterServices = Object.keys(services).filter(key => {
        if (key === 'home') return true
        if (!isServiceEnabledByBuild(key as any)) return false;
        // Role checks are disabled for now: every logged-in user can access all services.
        return true;
      })
      state.filteredServices = filterServices
      if (filterServices.length > 0) state.selectedService = filterServices[0]
    },
    setSelectedRecordId: (state, params: { payload: string }) => {
      state.selectedRecordId = params.payload
    },
    setZoom: (state, params: { payload: number[] }) => {
      state.zoom = params.payload
    },
    setNowPosition: (state, params: { payload: number }) => {
      state.nowPosition = params.payload
    },
    setZoomFactor: (state, params: { payload: number }) => {
      state.zoomFactor = params.payload
    },
    setScrollPosition: (state, params: { payload: number }) => {
      state.scrollPosition = params.payload
    },
    setSelectedChannel: (state, params: { payload: string | null }) => {
      state.selectedChannel = params.payload
    },
    setDragItemId: (state, params: { payload: string | null }) => {
      state.dragItemId = params.payload
    },
    setSelectedDate: (state, params: { payload: string }) => {
      state.selectedDate = params.payload
    },
    setCreateFromDrawRecord: (state) => {
      // if (current(state).editMode) {
      let drawRecord = current(state).drawRecord
      let new_record: IRecord = { ...emptyRecord, startedAt: drawRecord.startTime, monitor: drawRecord.channel, endedAt: drawRecord.endTime }
      state.editedRecords["new_0"] = new_record
      state.drawMode = false
      state.drawRecord = emptyDrawRecord
      console.log(new_record);
      // }
    },
    cleanEditedRecords: (state) => {
      state.editedRecords = {}
    },
    setDrawMode: (state, params: { payload: boolean }) => {
      state.drawMode = params.payload
    },
    setDrawRecord: (state, params: { payload: IDrawEvent }) => {
      state.drawRecord = params.payload
    },
    setViewMode: (state, params: { payload: IViewMode }) => {
      state.viewMode = params.payload
    },
    initNewRecord: (state) => {
      let event = current(state).editedRecords["new_0"]
      if (!event) state.editedRecords["new_0"] = emptyRecord
    },
    addRecord: (state, params: { payload: IRecord }) => {
      state.records = [...state.records, params.payload]
      state.editedRecords = {}
    },
    updateRecord: (state, params: { payload: IRecord }) => {
      state.records = state.records.map(r => r.id === params.payload.id ? params.payload : r)
    },
    deleteRecord: (state, params: { payload: string }) => {
      state.records = state.records.filter(r => r.id !== params.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedules.pending, (state) => {
        state.schedulerStatus = 'loading';
        state.schedulerError = null;
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedulerStatus = 'idle';
        const { schedules } = action.payload;
        state.schedules = schedules ?? [];
        state.records = mapSchedulesToRecords(state.schedules);
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.schedulerStatus = 'failed';
        state.schedulerError = action.error?.message ?? "Failed to fetch schedules";
      })

      .addCase(fetchScheduleById.pending, (state) => {
        state.schedulerStatus = 'loading';
        state.schedulerError = null;
      })
      .addCase(fetchScheduleById.fulfilled, (state, action) => {
        state.schedulerStatus = 'idle';
        const { schedule } = action.payload;
        state.selectedSchedule = schedule ?? null;
        if (schedule?.id) {
          const remaining = state.schedules.filter(existing => existing.id !== schedule.id);
          state.schedules = [...remaining, schedule];
          state.records = mapSchedulesToRecords(state.schedules);
        }
      })
      .addCase(fetchScheduleById.rejected, (state, action) => {
        state.schedulerStatus = 'failed';
        state.schedulerError = action.error?.message ?? "Failed to fetch schedule";
      })
      .addCase(fetchSchedulerProfiles.fulfilled, (state, action) => {
        state.schedulerProfiles = action.payload?.profiles ?? [];
      })
      .addCase(fetchSchedulerProfiles.rejected, (state) => {
        state.schedulerProfiles = [];
      })
      .addCase(fetchSchedulerDeviceCatalogue.fulfilled, (state, action) => {
        state.schedulerDeviceCatalogue = action.payload?.catalogue ?? [];
      })
      .addCase(fetchSchedulerDeviceCatalogue.rejected, (state) => {
        state.schedulerDeviceCatalogue = [];
      })
      .addCase(fetchSchedulerSlots.fulfilled, (state, action) => {
        state.schedulerSlots = action.payload?.slots ?? [];
      })
      .addCase(fetchSchedulerSlots.rejected, (state) => {
        state.schedulerSlots = [];
      })
      .addCase(createSchedulerSlot.fulfilled, (state, action) => {
        if (action.payload?.slot) {
          state.schedulerSlots = [...state.schedulerSlots, action.payload.slot];
        }
      })
      .addCase(updateSchedulerSlot.fulfilled, (state, action) => {
        if (action.payload?.slot) {
          state.schedulerSlots = state.schedulerSlots.map((s) =>
            s.id === action.payload.slot.id ? action.payload.slot : s
          );
        }
      })
      .addCase(deleteSchedulerSlot.fulfilled, (state, action) => {
        state.schedulerSlots = state.schedulerSlots.filter((s) => s.id !== action.payload.slotId);
        const next = { ...state.slotActiveConfigs };
        delete next[action.payload.slotId];
        state.slotActiveConfigs = next;
      })
      .addCase(fetchSlotActiveConfigs.fulfilled, (state, action) => {
        state.slotActiveConfigs = { ...state.slotActiveConfigs, ...action.payload };
      })

      .addCase(createSchedule.pending, (state) => {
        state.schedulerStatus = 'loading';
        state.schedulerError = null;
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        state.schedulerStatus = 'idle';
        const { schedule } = action.payload;
        if (schedule) {
          state.schedules = [...state.schedules.filter(existing => existing.id !== schedule.id), schedule];
          state.records = mapSchedulesToRecords(state.schedules);
        }
      })
      .addCase(createSchedule.rejected, (state, action) => {
        state.schedulerStatus = 'failed';
        state.schedulerError = action.error?.message ?? "Failed to create schedule";
      })

      .addCase(updateSchedule.pending, (state) => {
        state.schedulerStatus = 'loading';
        state.schedulerError = null;
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.schedulerStatus = 'idle';
        const { schedule } = action.payload;
        if (schedule?.id) {
          state.schedules = state.schedules.map(existing => existing.id === schedule.id ? schedule : existing);
          state.records = mapSchedulesToRecords(state.schedules);
          if (state.selectedSchedule?.id === schedule.id) {
            state.selectedSchedule = schedule;
          }
        }
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.schedulerStatus = 'failed';
        state.schedulerError = action.error?.message ?? "Failed to update schedule";
      })

      .addCase(deleteSchedule.pending, (state) => {
        state.schedulerStatus = 'loading';
        state.schedulerError = null;
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        state.schedulerStatus = 'idle';
        const { scheduleId } = action.payload;
        if (scheduleId) {
          state.schedules = state.schedules.filter(schedule => schedule.id !== scheduleId);
          state.records = mapSchedulesToRecords(state.schedules);
          if (state.selectedSchedule?.id === scheduleId) {
            state.selectedSchedule = null;
          }
        }
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.schedulerStatus = 'failed';
        state.schedulerError = action.error?.message ?? "Failed to delete schedule";
      })

      .addCase(loadTasks.pending, (state) => {
        state.statusOneGUI = 'loading';
      })
      .addCase(loadTasks.fulfilled, (state, action) => {
        state.statusOneGUI = 'idle';
        const { result }: { result: IJobsResponse } = action.payload;
        console.log("result", result);
        state.oneGUITasks = result
        // .items.map((item) => ({
        //   id: item.id,
        //   name: item.title,
        //   progress: item.progressPct,
        //   status: item.status,
        //   priority: item.priority,
        //   createdAt: item.submittedAt,
        //   startTime: item.startedAt,
        //   eta: item.eta,
        //   system: item.meta?.node || 'N/A',
        //   details: `Input: ${item.inputUri}, Output: ${item.outputUri}`,
        // }));
        //
        // console.log(result);
      })

      .addCase(loadWatchfolders.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(loadWatchfolders.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { watchfolderResult } = action.payload;
        const existingById: Record<string, IWatchfolder> = {};
        (state.watchfolderResult.content ?? []).forEach(w => { existingById[w.id] = w; });
        watchfolderResult.content = (watchfolderResult.content ?? []).map(item => {
          const existing = existingById[item.id];
          if (!existing) return item;
          const merged = { ...existing };
          (Object.keys(item) as (keyof IWatchfolder)[]).forEach(k => {
            const v = item[k];
            if (v !== undefined && v !== null) (merged as any)[k] = v;
          });
          return merged;
        });
        state.watchfolderResult = watchfolderResult;
      })

      .addCase(saveWatchfolder.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(saveWatchfolder.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { watchfolder, isNew } = action.payload;
        const content = state.watchfolderResult.content ?? [];
        state.watchfolderResult.content = isNew
          ? [...content, watchfolder]
          : content.map(w => w.id === watchfolder.id ? watchfolder : w);
      })

      .addCase(getWatchfolderEngines.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(getWatchfolderEngines.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { engines } = action.payload;
        state.watchfolderEngines = engines
      })

      .addCase(getWatchfolderEngineProfiles.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(getWatchfolderEngineProfiles.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { engineId, profiles } = action.payload;
        const watchfolderEngineProfiles = state.watchfolderEngineProfiles
        state.watchfolderEngineProfiles = { ...watchfolderEngineProfiles, [engineId]: profiles }
      })

      .addCase(loadFileQueuerLocations.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(loadFileQueuerLocations.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { locations } = action.payload;
        state.filequeuerLocations = locations
      })

      .addCase(loadFileQueuerLocationContent.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(loadFileQueuerLocationContent.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { content, path } = action.payload;
        // console.log("----> loadFileQueuerLocationContent", content, path);
        state.fileQueuerContent = { ...state.fileQueuerContent, [path ?? "root"]: content };
        // console.log("loadFileQue
      })

      .addCase(submitFileQueuerJob.pending, (state) => {
        state.statusWatchfolder = 'loading';
      })
      .addCase(submitFileQueuerJob.fulfilled, (state, action) => {
        state.statusWatchfolder = 'idle';
        const { content } = action.payload;
        // console.log("----> submitFileQueuerJob", content);
      })

      .addCase(loadIngestJobs.pending, (state) => {
        state.statusIngest = 'loading';
      })
      .addCase(loadIngestJobs.fulfilled, (state, action) => {
        state.statusIngest = 'idle';
        const { ingestResult } = action.payload;
        state.ingestResult = ingestResult;
      })
      .addCase(loadIngestJobs.rejected, (state) => {
        state.statusIngest = 'failed';
      })

      .addCase(submitAvidIngestJobs.pending, (state) => {
        state.statusIngest = 'loading';
      })
      .addCase(submitAvidIngestJobs.fulfilled, (state, action) => {
        state.statusIngest = 'idle';
        const jobs = action.payload.jobs ?? [];
        if (jobs.length > 0) {
          const content = [...jobs, ...state.ingestResult.content];
          state.ingestResult = {
            ...state.ingestResult,
            content,
            totalElements: content.length,
            size: content.length,
          };
        }
      })
      .addCase(submitAvidIngestJobs.rejected, (state) => {
        state.statusIngest = 'failed';
      })

      .addCase(loadIngestLocations.pending, (state) => {
        state.statusIngest = 'loading';
      })
      .addCase(loadIngestLocations.fulfilled, (state, action) => {
        state.statusIngest = 'idle';
        const { locations } = action.payload;
        state.ingestLocations = locations;
      })

      .addCase(loadIngestLocationContent.pending, (state) => {
        state.statusIngest = 'loading';
      })
      .addCase(loadIngestLocationContent.fulfilled, (state, action) => {
        state.statusIngest = 'idle';
        const { content, path } = action.payload;
        state.ingestContent = { ...state.ingestContent, [path ?? "root"]: content };
      })

  },
});

export const {
  setToken,
  setInitializing,
  setSessionExpired,
  setMe,
  setScrHeight,
  setSelectedService,
  setUpdateActionStamp,
  filterServices,
  setSelectedFileQueuerLocation,
  setSelectedFileQueuerPath,
  setFileQueuerTagetConfigs,
  setSelectedIngestLocation,
  setSelectedIngestPath,
  setSelectedSchedule,
  setSelectedRecordId,
  setSelectedChannel,
  setZoomFactor,
  setScrollPosition,
  setZoom,
  setNowPosition,
  setDragItemId,
  setSelectedDate,
  setCreateFromDrawRecord,
  setDrawMode,
  setDrawRecord,
  setViewMode,
  initNewRecord,
  addRecord,
  updateRecord,
  deleteRecord,
  cleanEditedRecords,
} = AppSlice.actions;

// export const selectMe = (state: RootState) => state.app.user;
export const selectToken = (state: RootState) => state.app.token;
export const selectSessionExpired = (state: RootState) => state.app.sessionExpired;
export const selectMe = (state: RootState) => state.app.me;
export const selectStatus = (state: RootState) => state.app.status;
export const selectFilteredServices = (state: RootState) => state.app.filteredServices;
export const selectInitializing = (state: RootState) => state.app.initializing;
export const selectWfResult = (state: RootState) => state.app.wfResult;
export const selectScrHeight = (state: RootState) => state.app.scrHeight;
export const selectUpdateActionsStamp = (state: RootState) => state.app.updateActionStamp;
export const selectSelectedService = (state: RootState) => state.app.selectedService;
export const selectOneGUITasks = (state: RootState) => state.app.oneGUITasks;
export const selectStatusOneGUI = (state: RootState) => state.app.statusOneGUI;
export const selectWatchfolderResult = (state: RootState) => state.app.watchfolderResult;
export const selectStatusWatchfolder = (state: RootState) => state.app.statusWatchfolder;
export const selectWatchfolderEngines = (state: RootState) => state.app.watchfolderEngines;
export const selectWatchfolderEngineProfiles = (state: RootState) => state.app.watchfolderEngineProfiles;

export const selectQueuerLocations = (state: RootState) => state.app.filequeuerLocations;
export const selectFileQueuerContent = (state: RootState) => state.app.fileQueuerContent;
export const selectFileQueuerResult = (state: RootState) => state.app.filequeuerResult;
export const selectSelectedFileQueuerLocation = (state: RootState) => state.app.selectedFileQueuerLocation;
export const selectSelectedFileQueuerPath = (state: RootState) => state.app.selectedFileQueuerPath;
export const selectFileQueuerTagetConfigs = (state: RootState) => state.app.fileQueuerTagetConfigs;

export const selectIngestResult = (state: RootState) => state.app.ingestResult;
export const selectStatusIngest = (state: RootState) => state.app.statusIngest;
export const selectIngestLocations = (state: RootState) => state.app.ingestLocations;
export const selectIngestContent = (state: RootState) => state.app.ingestContent;
export const selectSelectedIngestLocation = (state: RootState) => state.app.selectedIngestLocation;
export const selectSelectedIngestPath = (state: RootState) => state.app.selectedIngestPath;
export const selectSchedules = (state: RootState) => state.app.schedules;
export const selectSchedulerSlots = (state: RootState) => state.app.schedulerSlots;
export const selectSlotActiveConfigs = (state: RootState) => state.app.slotActiveConfigs;
export const selectSchedulerProfiles = (state: RootState) => state.app.schedulerProfiles;
export const selectSchedulerDeviceCatalogue = (state: RootState) => state.app.schedulerDeviceCatalogue;
export const selectSelectedSchedule = (state: RootState) => state.app.selectedSchedule;
export const selectSchedulerStatus = (state: RootState) => state.app.schedulerStatus;
export const selectSchedulerError = (state: RootState) => state.app.schedulerError;

export const selectRecords = (state: RootState) => state.app.records;
export const selectEditedRecords = (state: RootState) => state.app.editedRecords;

export const selectSelectedRecordId = (state: RootState) => state.app.selectedRecordId;
export const selectZoom = (state: RootState) => state.app.zoom;
export const selectZoomFactor = (state: RootState) => state.app.zoomFactor;
export const selectScrollPosition = (state: RootState) => state.app.scrollPosition;
export const selectNowPosition = (state: RootState) => state.app.nowPosition;
export const selectDragItemId = (state: RootState) => state.app.dragItemId;
export const selectSelectedChannel = (state: RootState) => state.app.selectedChannel;
export const selectSelectedDate = (state: RootState) => state.app.selectedDate;
export const selectDrawMode = (state: RootState) => state.app.drawMode;
export const selectDrawRecord = (state: RootState) => state.app.drawRecord;

export const selectPermissions = (state: RootState) => state.app.permissions;

export const ADMIN_PERMISSION = 'gateway:admin';
export const selectIsAdmin = (state: RootState) => !!state.app.me?.permissions?.includes(ADMIN_PERMISSION);

export default AppSlice.reducer;
